// Host-layer telemetry seam (INC-098 / ADR-053): PostHog EU, anonymous + cookieless.
//
// Boundary rule (ADR-012 pattern, SPEC FR-1): `posthog-js` is imported ONLY here and
// wired from `src/main.ts`. Nothing under `src/engine/`, `src/stores/`, or any
// content pack may import it. `identify` is never exposed or called (FR-2).
//
// Perf (INC-106): the SDK is loaded via dynamic `import()` inside `initTelemetry()`
// so it forms its own async chunk instead of shipping ~78 KiB inside the
// render-blocking initial `app-*.js` bundle. The FR-6 guard runs BEFORE the import,
// so dev/Vitest/keyless builds never even fetch the chunk. Type-only import below is
// erased at build and does not undo the split.
import type { PostHog } from 'posthog-js'

// Set only after `posthog.init()` has run — the capture functions below no-op on
// null, which keeps them safe to call synchronously from `router.afterEach` while
// the SDK chunk is still loading (or forever, when FR-6 disables telemetry).
let posthog: PostHog | null = null
let initStarted = false

/**
 * Initialize PostHog. Kill-switch by configuration (FR-6): a no-op unless this is a
 * production build AND `VITE_POSTHOG_KEY` is set — dev server, Vitest, CI and any
 * white-label build without the env var emit nothing (and never download the SDK).
 * Resolves once the SDK is loaded and initialized; `src/main.ts` awaits it before
 * capturing the initial `$pageview`.
 */
export async function initTelemetry(): Promise<void> {
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (initStarted || !import.meta.env.PROD || !key) return
  initStarted = true
  const { default: ph } = await import('posthog-js')
  ph.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com', // NFR-1 EU residency
    person_profiles: 'identified_only', // FR-2 anonymous-only
    // NB: capture is opted-in by default here (anonymous + memory persistence, no consent
    // banner per NFR-3). Do NOT set `opt_out_capturing_by_default: true` — with no matching
    // `opt_in_capturing()` call it silences ALL capture, including Session Replay.
    persistence: 'memory', // NFR-3 cookieless, no consent banner needed
    capture_pageview: false, // FR-4: pageviews come from the router hook, not the SDK default
    // FIX: pageleave defaults to 'if_capture_pageview', which resolves to OFF because
    // `capture_pageview` is false. Force it on so tab-close / site-exit emits `$pageleave`
    // — the signal PostHog needs for accurate bounce rate and session duration.
    capture_pageleave: true,
    // Perf (INC-106, ADR-053 amendment): recording does NOT start at load — that pulled
    // ~54 KiB of posthog-recorder.js plus a 60 ms long task into every visit. It is
    // started on the first user interaction below; idle bounces record nothing.
    disable_session_recording: true,
    session_recording: { maskAllInputs: true }, // FR-3 replay with input masking (D-2 default)
    // Perf (INC-106): no active surveys — skips the ~33 KiB surveys.js fetch. Flip this
    // (one line) when a PostHog survey is actually launched.
    disable_surveys: true,
  })
  posthog = ph
  // Lazy-start Session Replay on the first real interaction. `pointerdown` covers
  // mouse and touch (incl. the start of a mobile scroll); `keydown` covers keyboard
  // users. Only the pre-interaction seconds of each replay are lost.
  const startReplay = () => {
    window.removeEventListener('pointerdown', startReplay)
    window.removeEventListener('keydown', startReplay)
    ph.startSessionRecording()
  }
  window.addEventListener('pointerdown', startReplay, { passive: true })
  window.addEventListener('keydown', startReplay)
}

/**
 * Capture a `$pageview` (FR-4). Called from `router.afterEach` for SPA route changes
 * and once after init for the initial load. No-op until `initTelemetry()` resolved (FR-6).
 */
export function capturePageview(): void {
  posthog?.capture('$pageview')
}

/**
 * Capture a `$pageleave` for the page the user is navigating away from (FR-4).
 * posthog-js only auto-emits `$pageleave` on a real browser unload (tab close /
 * hard navigation) — never on SPA route changes — so the router hook calls this
 * for each in-app transition. Pass the *leaving* page's absolute URL, because by
 * the time `router.afterEach` runs `window.location` already points at the new
 * route; the explicit `$current_url` keeps the event attributed to the old page.
 * No-op until `initTelemetry()` resolved (FR-6).
 */
export function capturePageleave(url?: string): void {
  posthog?.capture('$pageleave', url ? { $current_url: url } : undefined)
}
